import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Fade,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { 
  Google as GoogleIcon,
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';

const LoginPage: React.FC = () => {
  const { state, loginWithGoogle, clearError } = useAppContext();

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
    } catch (error) {
      // Error handling is done in the context
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Fade in timeout={1000}>
          <Card
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              backdropFilter: 'blur(10px)',
              background: 'rgba(255, 255, 255, 0.95)',
              maxWidth: 400,
              mx: 'auto',
            }}
          >
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              {/* Minimal Logo */}
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #4285f4 0%, #1976d2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  >
                    📊
                  </Typography>
                </Box>
                
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: '#2c3e50',
                    mb: 1,
                  }}
                >
                  歡迎使用
                </Typography>
                
                <Typography
                  variant="body2"
                  sx={{
                    color: '#7a8a99',
                  }}
                >
                  請使用 Google 帳戶登入
                </Typography>
              </Box>

              {/* Error Alert */}
              {state.error && (
                <Alert 
                  severity="error" 
                  sx={{ mb: 3, textAlign: 'left' }}
                  onClose={clearError}
                >
                  {state.error.message}
                </Alert>
              )}

              {/* Google Login Button */}
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleGoogleLogin}
                disabled={state.isLoading}
                startIcon={<GoogleIcon />}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  borderRadius: 2,
                  borderColor: '#dadce0',
                  color: '#3c4043',
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                    borderColor: '#dadce0',
                  },
                  '&:disabled': {
                    backgroundColor: '#f8f9fa',
                  },
                }}
              >
                {state.isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  '使用 Google 帳戶登入'
                )}
              </Button>

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 3,
                  color: '#7a8a99',
                  fontSize: '0.8rem',
                  lineHeight: 1.4,
                }}
              >
                登入即表示您同意我們的服務條款和隱私政策
              </Typography>
            </CardContent>
          </Card>
        </Fade>
      </Container>

      {/* 裝飾性背景元素 */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          zIndex: -1,
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '200%',
            height: '200%',
            background: `
              radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(120, 200, 255, 0.3) 0%, transparent 50%)
            `,
            animation: 'float 20s ease-in-out infinite',
          }
        }}
      />

      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            33% { transform: translate(30px, -30px) rotate(120deg); }
            66% { transform: translate(-20px, 20px) rotate(240deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default LoginPage;
